import { useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import s from "./Projects.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import {
  useProjectInspection,
  useRecordInspectionItems,
  useRecordInspectionResult,
  useReleaseHoldPoint,
} from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  INSPECTION_KIND_LABEL,
  INSPECTION_RESULT_LABEL,
  INSPECTION_RESULTS,
  ITEM_RESULT_LABEL,
  ITEM_RESULTS,
  releaseBlockers,
  resultBlockedByUncheckedItems,
  type InspectionItem,
  type InspectionResult,
  type InspectionWriteResult,
  type ItemResult,
  type ProjectInspectionDetail,
} from "../../domain/projects";
import { Button, Card, ErrorState, Input, numClass, Select, Skeleton, Table } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t } from "../../i18n";

/**
 * One inspection sheet, and the two acts the projects pack keeps apart.
 *
 * Recording what was found is `project:write`. Letting the work continue past
 * a hold point is `project:release_hold`. On a real site those are two people,
 * and this screen offers them as two controls with two permissions and two
 * explanations — collapsing them into one "complete" button would remove the
 * only thing a hold point does.
 */
export function InspectionSheetPage() {
  const { id = "" } = useParams();
  const inspection = useProjectInspection(id);

  if (inspection.isPending) return <Skeleton rows={8} height={20} />;
  if (inspection.isError) return <ErrorState error={inspection.error} retry={() => void inspection.refetch()} />;

  const i = inspection.data;
  return (
    <Page
      eyebrow={`${i.projectCode} · ${i.reference}`}
      title={i.title}
      actions={<Link to={`/capital-projects/${i.projectId}`}>← {i.projectCode}</Link>}
    >
      {i.stoppingWork && (
        <div className={s.stopBanner} data-testid="sheet-holding">
          <div className={s.stopTitle}>{t("This hold point is stopping the work")}</div>
          <div className={s.note}>{i.holdingBecause}</div>
        </div>
      )}
      {i.releasedAt && (
        <div className={s.note} style={{ marginBottom: 12 }} data-testid="sheet-released">
          {i.releasedByName
            ? t("Released {date} by {name} — the work may continue past this point.", {
                date: formatDateTime(i.releasedAt),
                name: i.releasedByName,
              })
            : t("Released {date} — the work may continue past this point.", { date: formatDateTime(i.releasedAt) })}
        </div>
      )}

      <div className={s.split}>
        <Card title="Inspection">
          <dl className={s.facts}>
            <dt>{t("Kind")}</dt>
            <dd>
              <span
                className={s.state}
                style={{ "--st-c": i.kind === "hold" ? "var(--red)" : "var(--muted)" } as CSSProperties}
              >
                {t(INSPECTION_KIND_LABEL[i.kind])}
              </span>
            </dd>
            <dt>{t("Discipline")}</dt>
            <dd>{i.discipline}</dd>
            <dt>{t("Location")}</dt>
            <dd>{i.location ?? "—"}</dd>
            <dt>{t("Scheduled")}</dt>
            <dd>{i.scheduledFor ? formatDateTime(i.scheduledFor) : "—"}</dd>
            <dt>{t("Performed")}</dt>
            <dd>
              {i.performedAt ? formatDateTime(i.performedAt) : "—"}
              {i.performedByName ? ` ${t("by {name}", { name: i.performedByName })}` : ""}
            </dd>
            <dt>{t("Witnessed")}</dt>
            <dd>{i.witnessedBy ?? "—"}</dd>
          </dl>
          {i.notes && (
            <div className={s.said} style={{ marginTop: 8 }}>
              {i.notes}
            </div>
          )}
        </Card>

        <Card title="Sheet" sub={i.quality.why ?? undefined}>
          <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
            <div>
              <div className={s.figure}>
                {i.quality.items - i.quality.notChecked}/{i.quality.items}
              </div>
              <div className={s.moneyLabel}>{t("answered")}</div>
            </div>
            <div>
              <div className={s.figure} style={{ color: i.quality.failed ? "var(--red)" : undefined }}>
                {i.quality.failed}
              </div>
              <div className={s.moneyLabel}>{t("failed")}</div>
            </div>
            <div>
              <span className={s.state} style={{ "--st-c": qualityColour(i.quality.state) } as CSSProperties}>
                {t(i.quality.state.replace(/_/g, " "))}
              </span>
            </div>
          </div>
          {/* `complete` says every item was answered. Whether the answers were
              passes is `failed`, reported separately and never folded in. */}
          <div className={s.said} style={{ marginTop: 8 }}>
            {t(
              "Complete means every item was answered, including “not applicable.” It doesn't mean the inspection passed.",
            )}
          </div>
        </Card>
      </div>

      <SheetCard i={i} />
      <ResultCard i={i} />
    </Page>
  );
}

function SheetCard({ i }: { i: ProjectInspectionDetail }) {
  const { principal } = useSession();
  const record = useRecordInspectionItems(i.id);
  const mayWrite = hasPermission(principal, PERMISSION.projectsWrite);
  const frozen = Boolean(i.releasedAt);

  const answer = (item: InspectionItem, result: ItemResult) => record.mutate([{ seq: item.seq, result }]);

  if (i.sheet.length === 0)
    return (
      <Card title="Items">
        <div className={s.said}>
          {t("This inspection has no items. That doesn't mean nothing was wrong.")}
        </div>
      </Card>
    );

  return (
    <Card
      title="Items"
      sub={
        frozen
          ? "the sheet is frozen: changing answers now would rewrite the sheet the release was given against"
          : "an unchecked item is not a pass"
      }
    >
      {record.isError && (
        <div className={s.bad} role="alert">
          {t("Refused: {message}", { message: (record.error as Error).message })}
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <th style={{ width: 34 }}>#</th>
            <th>{t("Item")}</th>
            <th style={{ width: 120 }}>{t("Result")}</th>
            <th style={{ width: 260 }}>{t("Answer")}</th>
          </tr>
        </thead>
        <tbody>
          {i.sheet.map((item) => (
            <tr key={item.seq} className={s.sheetRow} data-testid={`item-${item.seq}`}>
              <td className={numClass}>{item.seq}</td>
              <td>
                <div className={s.prompt}>{item.prompt ?? item.itemKey ?? t("Item {n}", { n: item.seq })}</div>
                {item.note && <div className={s.itemNote}>{item.note}</div>}
                {item.defectId && (
                  <div className={s.itemNote} style={{ color: "var(--amber)" }}>
                    {t("Defect raised on the register")}
                    {item.defectStatus ? ` — ${item.defectStatus}` : ""}
                  </div>
                )}
              </td>
              <td>
                <span className={s.state} style={{ "--st-c": itemColour(item.result) } as CSSProperties}>
                  {t(ITEM_RESULT_LABEL[item.result])}
                </span>
              </td>
              <td>
                <div className={s.answers}>
                  {ITEM_RESULTS.filter((r) => r !== "not_checked").map((r) => (
                    <Button
                      key={r}
                      size="sm"
                      variant={item.result === r ? "primary" : "default"}
                      disabled={!mayWrite || frozen || record.isPending}
                      title={mayWrite ? undefined : t("Needs the project:write permission")}
                      onClick={() => answer(item, r)}
                      aria-label={t("{result} item {n}", { result: t(ITEM_RESULT_LABEL[r]), n: item.seq })}
                    >
                      {t(ITEM_RESULT_LABEL[r])}
                    </Button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/**
 * The two acts, side by side and never merged: record the result, then — a
 * separate decision, by a separate person, on a separate permission — release
 * the hold point.
 */
function ResultCard({ i }: { i: ProjectInspectionDetail }) {
  const { principal } = useSession();
  const [result, setResult] = useState<InspectionResult>(i.result ?? "pass");
  const [notes, setNotes] = useState("");
  const [releaseNote, setReleaseNote] = useState("");
  const [written, setWritten] = useState<InspectionWriteResult | null>(null);

  const recordResult = useRecordInspectionResult(i.id);
  const release = useReleaseHoldPoint(i.id);

  const mayWrite = hasPermission(principal, PERMISSION.projectsWrite);
  const mayRelease = hasPermission(principal, PERMISSION.projectsReleaseHold);
  const blockedResult = resultBlockedByUncheckedItems(result, i.quality);
  const blockers = releaseBlockers({
    kind: i.kind,
    result: i.result,
    releasedAt: i.releasedAt,
    quality: i.quality,
  });

  return (
    <div className={s.split}>
      <Card title="Record the result" sub="what the inspection found. This does not release a hold point.">
        <div style={{ display: "grid", gap: 10 }}>
          <Select
            value={result}
            onChange={(e) => setResult(e.target.value as InspectionResult)}
            aria-label={t("Result")}
            disabled={Boolean(i.releasedAt)}
          >
            {INSPECTION_RESULTS.map((r) => (
              <option key={r} value={r}>
                {t(INSPECTION_RESULT_LABEL[r])}
              </option>
            ))}
          </Select>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("Notes — what was found, measured or agreed")}
            aria-label={t("Notes")}
            disabled={Boolean(i.releasedAt)}
          />
          {blockedResult && (
            <div className={s.warn} data-testid="result-blocked">
              {t(
                "{n} of {total} items are unchecked, so “{result}” will be refused. Answer them, or pick a result that doesn't say the work passed.",
                { n: i.quality.notChecked, total: i.quality.items, result: t(INSPECTION_RESULT_LABEL[result]) },
              )}
            </div>
          )}
          <Button
            variant="primary"
            disabled={!mayWrite || Boolean(i.releasedAt) || recordResult.isPending}
            title={mayWrite ? undefined : t("Needs the project:write permission")}
            onClick={() =>
              recordResult.mutate({ result, notes: notes.trim() || undefined }, { onSuccess: (r) => setWritten(r) })
            }
          >
            {recordResult.isPending ? t("Recording…") : t("Record result")}
          </Button>
          {recordResult.isError && (
            <div className={s.bad} role="alert">
              {t("Refused: {message}", { message: (recordResult.error as Error).message })}
            </div>
          )}
          {written?.stillStoppingWork && (
            <div className={s.warn} role="status" data-testid="still-stopping">
              {written.why}
            </div>
          )}
          {written?.warnings.map((w, n) => (
            <div key={n} className={s.warn} role="status">
              {w}
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="Release the hold point"
        sub="a separate act, separately permissioned and audited every time — this is the signature the hold point was placed to require"
      >
        {i.kind !== "hold" ? (
          <div className={s.said}>
            {t("{kind} — this does not stop the work, so it has nothing to release.", {
              kind: t(INSPECTION_KIND_LABEL[i.kind]),
            })}
          </div>
        ) : i.releasedAt ? (
          <div className={s.said}>
            {i.releasedByName
              ? t("Released {date} by {name}.", { date: formatDate(i.releasedAt), name: i.releasedByName })
              : t("Released {date}.", { date: formatDate(i.releasedAt) })}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <Input
              value={releaseNote}
              onChange={(e) => setReleaseNote(e.target.value)}
              placeholder={t("Note against the release (optional)")}
              aria-label={t("Release note")}
            />
            {blockers.length > 0 && (
              <div className={s.blockers} data-testid="release-blockers">
                {blockers.map((b, n) => (
                  <div key={n} className={s.blocker}>
                    {b}
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="danger"
              disabled={!mayRelease || blockers.length > 0 || release.isPending}
              title={
                mayRelease
                  ? undefined
                  : t("Needs the project:release_hold permission — releasing a hold point is not recording a result")
              }
              onClick={() => release.mutate(releaseNote.trim() || undefined, { onSuccess: (r) => setWritten(r) })}
            >
              {release.isPending ? t("Releasing…") : t("Release — work may continue")}
            </Button>
            {release.isError && (
              <div className={s.bad} role="alert">
                {t("Refused: {message}", { message: (release.error as Error).message })}
              </div>
            )}
            {!mayRelease && (
              <div className={s.said}>
                {t("Releasing needs")} <span className="mono">project:release_hold</span>
                {t(
                  ", which you don't have, so the button is hidden.",
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function itemColour(result: ItemResult): string {
  if (result === "pass") return "var(--green)";
  if (result === "fail") return "var(--red)";
  if (result === "not_applicable") return "var(--dim)";
  return "var(--muted)";
}

function qualityColour(state: ProjectInspectionDetail["quality"]["state"]): string {
  if (state === "complete") return "var(--green)";
  if (state === "incomplete") return "var(--amber)";
  return "var(--dim)";
}
